import { ChevronLeft, ChevronRight } from 'lucide-react';

/** Local-timezone date key (YYYY-MM-DD) — matches how slot times are shown to the user. */
export function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface MonthCalendarProps {
  /** Any date within the month to display. */
  month: Date;
  onMonthChange: (firstOfMonth: Date) => void;
  /** Set of YYYY-MM-DD keys that have bookable slots. */
  availableKeys: Set<string>;
  selectedKey: string;
  onSelect: (key: string, date: Date) => void;
  /** Navigable bounds (first-of-month). */
  minMonth: Date;
  maxMonth: Date;
}

/**
 * Compact, fully-tokenized month date-picker. Available days read as filled chips; unavailable
 * days are dimmed and non-interactive; the selected day is the primary accent. No gold text —
 * `bg-primary` is a background, and day numbers stay `text-foreground` / `text-primary-foreground`.
 * Works full-width on mobile (stacked above the time list) and in a column on desktop.
 */
export function MonthCalendar({
  month,
  onMonthChange,
  availableKeys,
  selectedKey,
  onSelect,
  minMonth,
  maxMonth,
}: MonthCalendarProps) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstOfMonth = new Date(year, m, 1);
  const startWeekday = firstOfMonth.getDay();
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const todayKey = dateKey(new Date());
  const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(firstOfMonth);

  const canPrev = firstOfMonth > minMonth;
  const canNext = firstOfMonth < maxMonth;

  const cells: Array<Date | null> = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, m, d));

  // 44px touch targets on mobile (the only way to change months on a phone), compact on desktop.
  const navBtn =
    'grid h-11 w-11 md:h-8 md:w-8 place-items-center rounded-md border border-border text-muted-foreground transition-colors md:hover:bg-muted disabled:pointer-events-none disabled:opacity-40';

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">{monthLabel}</span>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous month" disabled={!canPrev} className={navBtn}
            onClick={() => onMonthChange(new Date(year, m - 1, 1))}>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Next month" disabled={!canNext} className={navBtn}
            onClick={() => onMonthChange(new Date(year, m + 1, 1))}>
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w, i) => (
          <span key={i} className="py-1 text-xs font-medium text-muted-foreground">{w}</span>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((date, i) => {
          if (!date) return <span key={`pad-${i}`} aria-hidden="true" />;
          const key = dateKey(date);
          const available = availableKeys.has(key);
          const selected = key === selectedKey;
          const isToday = key === todayKey;
          return (
            <button
              key={key}
              type="button"
              disabled={!available}
              aria-pressed={selected}
              aria-label={date.toDateString()}
              onClick={() => onSelect(key, date)}
              className={[
                'grid h-11 w-full md:h-10 place-items-center rounded-md text-sm transition-colors',
                selected
                  ? 'bg-primary font-semibold text-primary-foreground'
                  : available
                    ? `bg-secondary text-foreground md:hover:bg-muted ${isToday ? 'font-semibold' : ''}`
                    : 'pointer-events-none text-muted-foreground/40',
              ].join(' ')}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default MonthCalendar;
